import { useEffect, useRef, useState } from 'react';
import { useBasket } from '../context/BasketContext.jsx';
import { useStoreUI } from '../context/StoreUIContext.jsx';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { avatarUrl } from '../lib/minecraft.js';
import '../styles/components/LoginModalStyle.css';


const USERNAME_PATTERN = /^\.?[A-Za-z0-9_]{2,17}$/;

const SKIN_PREVIEW_DELAY_MS = 200;

export function LoginModal() {
  const { isLoginOpen, closeLogin } = useStoreUI();
  const { login } = useBasket();

  const [username, setUsername] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [error, setError] = useState('');
  const [isShaking, setShaking] = useState(false);
  const inputRef = useRef(null);

  useEscapeKey(closeLogin, isLoginOpen);

  useEffect(() => {
    if (!isLoginOpen) return undefined;

    const timer = setTimeout(() => inputRef.current?.focus(), 220);

    return () => clearTimeout(timer);
  }, [isLoginOpen]);


  useEffect(() => {
    const timer = setTimeout(() => setPreviewName(username.trim()), SKIN_PREVIEW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [username]);

  const rejectWith = (message) => {
    setError(message);
    setShaking(true);
    inputRef.current?.focus();
  };

  const submit = async () => {
    const trimmed = username.trim();

    if (!USERNAME_PATTERN.test(trimmed)) {
      rejectWith('Enter a valid Minecraft username (2–17 chars).');
      return;
    }

    const result = await login(trimmed);

    if (!result.success) {
      rejectWith(result.error || 'Login failed.');
      return;
    }

    setUsername('');
    setError('');
    closeLogin();
  };

  return (
    <div
      className={`login-modal${isLoginOpen ? ' active' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
      aria-hidden={!isLoginOpen}
      onClick={(event) => event.target === event.currentTarget && closeLogin()}
    >
      <div
        className={`login-box${isShaking ? ' shake' : ''}`}
        onAnimationEnd={() => setShaking(false)}
      >
        <button type="button" className="login-close" aria-label="Close" onClick={closeLogin}>
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="login-illustration">
          <img
            src={avatarUrl(previewName, 160)}
            className="username-head"
            alt=""
            width="80"
            height="80"
            decoding="async"
          />
          <div className="login-glow" aria-hidden="true" />
        </div>

        <h2 id="login-modal-title">Login to continue</h2>
        <p className="login-sub">
          Enter your Minecraft username — we&apos;ll deliver your perks to that account.
        </p>

        <div className="username-field">
          <i className="fa-solid fa-user" />
          <input
            ref={inputRef}
            type="text"
            autoComplete="off"
            spellCheck="false"
            placeholder="Your Minecraft username"
            maxLength={18}
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
          />
        </div>

        <p className="login-hint">
          <i className="fa-solid fa-circle-info" />
          Bedrock players: prefix your name with a <strong>.</strong>
        </p>

        <button type="button" className="login-button" onClick={submit}>
          <span>
            <i className="fa-solid fa-right-to-bracket" /> Continue
          </span>
        </button>

        <p className="login-error" hidden={!error}>
          {error}
        </p>
      </div>
    </div>
  );
}
